import React, { FC, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "hooks/useReduxStore";
import menuLoopDetailSelector from "store/selectors/menu/menuLoopDetailSelector";
import { menuLoopActions } from "store/slices/menu/menuLoopDetailSlice";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Grid, SelectChangeEvent, Stack, Tooltip } from "@mui/material";
import AdaptiveRowHeader from "components/common/ui/AdaptiveRowHeader";
import CircleIconButton from "components/common/ui/CircleIconButton";
import CloseIcon from "@mui/icons-material/Close";
import SpacedTextInput, {
  SpacedDatePicker,
  SpacedDropDownSelect,
} from "components/common/ui/SpacedTextInput";
import { MEAL_SERVING_TIME_OPTIONS } from "models/menu/constants/servingTimes";

const getHourByMealType = (mealType: string): number =>
  MEAL_SERVING_TIME_OPTIONS.find((option) => option.mealType === mealType)
    ?.hour ?? 17;

// special_date arrives from the server as "YYYY-MM-DDTHH:mm:ss±HH:MM" in the
// linked site's timezone — the wall-clock parts are what was stored naively in
// the database, so we drive UI and writes from those parts directly rather
// than letting dayjs convert through the browser's local timezone.
const getSpecialDateDayPart = (iso: string): string => iso.slice(0, 10);
const getSpecialDateHour = (iso: string): number => Number(iso.slice(11, 13));
const toTZNaiveISOString = (date: Dayjs, hour: number): string =>
  `${date.format("YYYY-MM-DD")}T${String(hour).padStart(2, "0")}:00:00`;

interface MenuLoopSpecialDateRowProps {
  displayUUID: string;
}

const MenuLoopSpecialDateRow: FC<MenuLoopSpecialDateRowProps> = ({
  displayUUID,
}) => {
  const editingMenuLoop = useAppSelector(
    menuLoopDetailSelector.editingMenuLoop,
  );
  const displaySpecialDates = useAppSelector(
    menuLoopDetailSelector.editingMenuLoopSpecialDates,
  );
  const editingSpecialDate = useAppSelector(
    menuLoopDetailSelector.getEditingMenuLoopSpecialDate,
  )(displayUUID);
  const dispatch = useAppDispatch();
  const [displayDescription, setDisplayDescription] = React.useState(
    editingSpecialDate?.description ?? "",
  );
  const [mealType, setMealType] = React.useState<string>(() => {
    if (editingSpecialDate?.special_date) {
      const hour = getSpecialDateHour(editingSpecialDate.special_date);
      return (
        MEAL_SERVING_TIME_OPTIONS.find((option) => option.hour === hour)
          ?.mealType ?? "N/A"
      );
    }
    return "N/A";
  });

  /**
   * Handle user delete special date row
   */
  const handleDeleteSpecialDateClick = useCallback(() => {
    const { [displayUUID]: deleteMenuLoop, ...rest } =
      displaySpecialDates ?? {};
    dispatch(
      menuLoopActions.setMenuLoopDetail({
        ...editingMenuLoop,
        skip_dates: rest,
      }),
    );
  }, [dispatch, displaySpecialDates, displayUUID, editingMenuLoop]);

  /**
   * Handle user change special date
   */
  const handleDateChange = useCallback(
    (newDate: Dayjs | null) => {
      const hour = getHourByMealType(mealType);
      dispatch(
        menuLoopActions.setMenuLoopDetail({
          ...editingMenuLoop,
          skip_dates: {
            ...displaySpecialDates,
            [displayUUID]: {
              ...editingSpecialDate,
              special_date: newDate ? toTZNaiveISOString(newDate, hour) : null,
            },
          },
        }),
      );
    },
    [
      dispatch,
      editingSpecialDate,
      displaySpecialDates,
      displayUUID,
      editingMenuLoop,
      mealType,
    ],
  );

  /**
   * Handle user change special date reason
   */
  const handleReasonChange = useCallback(
    (newDate: string | null) => {
      dispatch(
        menuLoopActions.setMenuLoopDetail({
          ...editingMenuLoop,
          skip_dates: {
            ...displaySpecialDates,
            [displayUUID]: {
              ...editingSpecialDate,
              description: newDate,
            },
          },
        }),
      );
    },
    [
      dispatch,
      editingSpecialDate,
      displaySpecialDates,
      displayUUID,
      editingMenuLoop,
    ],
  );

  /**
   * Handle user change meal type
   */
  const handleMealTypeChange = useCallback(
    (event: SelectChangeEvent) => {
      const newMealType = event.target.value;
      setMealType(newMealType);

      // Update the special_date with new time if date exists
      if (editingSpecialDate?.special_date) {
        const dayPart = getSpecialDateDayPart(editingSpecialDate.special_date);
        const hour = getHourByMealType(newMealType);
        dispatch(
          menuLoopActions.setMenuLoopDetail({
            ...editingMenuLoop,
            skip_dates: {
              ...displaySpecialDates,
              [displayUUID]: {
                ...editingSpecialDate,
                special_date: toTZNaiveISOString(dayjs(dayPart), hour),
              },
            },
          }),
        );
      }
    },
    [
      dispatch,
      editingSpecialDate,
      displaySpecialDates,
      displayUUID,
      editingMenuLoop,
    ],
  );

  /**
   * Validate if there is a duplicated special date
   */
  const hasDuplicatedSpecialDate = useCallback(
    () =>
      !!Object.entries(displaySpecialDates ?? {}).find(
        ([displayKey, specialDate]) =>
          displayKey !== displayUUID &&
          specialDate.special_date === editingSpecialDate?.special_date,
      ),
    [displaySpecialDates, displayUUID, editingSpecialDate?.special_date],
  );

  /**
   * Validate if special date out of loop date range
   */
  const isSpecialDateOutOfLoopingDateRange = useCallback(() => {
    const startDate = dayjs(editingMenuLoop?.start_date ?? null);
    const endDate = dayjs(editingMenuLoop?.end_date ?? null);
    const specialDate = dayjs(editingSpecialDate?.special_date ?? null);
    return !(
      !editingMenuLoop?.start_date ||
      !editingMenuLoop?.end_date ||
      !editingSpecialDate?.special_date ||
      (startDate <= specialDate && specialDate <= endDate)
    );
  }, [
    editingMenuLoop?.end_date,
    editingMenuLoop?.start_date,
    editingSpecialDate?.special_date,
  ]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Grid item xs={7} sm={8} md={4} lg={3}>
        <Stack spacing={1} direction="row">
          <AdaptiveRowHeader switchWidthInPixel={1}>
            <Tooltip title="Remove this row">
              <CircleIconButton
                positive
                danger
                sizepx={30}
                onClick={handleDeleteSpecialDateClick}
              >
                <CloseIcon />
              </CircleIconButton>
            </Tooltip>
          </AdaptiveRowHeader>
          <SpacedDatePicker
            label="Special date to skip"
            value={
              editingSpecialDate?.special_date
                ? dayjs(getSpecialDateDayPart(editingSpecialDate.special_date))
                : dayjs(null)
            }
            onChange={handleDateChange}
            slotProps={{
              textField: {
                helperText:
                  `${hasDuplicatedSpecialDate() ? "Duplicated." : ""} ` +
                  `${isSpecialDateOutOfLoopingDateRange() ? "Out of looping date range." : ""} `,
                error:
                  !editingSpecialDate?.special_date || // prompt error when empty
                  isSpecialDateOutOfLoopingDateRange() ||
                  hasDuplicatedSpecialDate(),
              },
              field: {
                clearable: !!editingSpecialDate?.special_date,
                onClear: () => {
                  handleDateChange(null);
                },
              },
            }}
          />
        </Stack>
      </Grid>
      <Grid item xs={5} sm={4} md={2}>
        <SpacedDropDownSelect<string>
          name="mealType"
          shrink
          label="Meal to skip"
          options={Object.fromEntries(
            MEAL_SERVING_TIME_OPTIONS.map((option) => [
              option.label,
              option.mealType,
            ]),
          )}
          value={mealType}
          onChange={handleMealTypeChange}
          error={mealType === "N/A"}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={7}>
        <SpacedTextInput
          label="Reason"
          value={displayDescription}
          onChange={(newValue) => {
            setDisplayDescription(newValue.target.value ?? "");
          }}
          onBlur={() => {
            handleReasonChange(displayDescription);
          }}
        />
      </Grid>
    </LocalizationProvider>
  );
};

export default MenuLoopSpecialDateRow;
